<script>
import { mdiLaptop } from '@mdi/js'
import gql from 'graphql-tag'
import DashboardSection from './dashboard_section.vue'

export default {
    name: 'RegisteredProductsSection',
    components: { DashboardSection },
    data() {
        return {
            products: [],
            loading: true,
            fetchingProducts: false,
            error: ''
        }
    },
    computed: {
        section() {
            return {
                id: 'products',
                title: 'Registered products',
                icon: mdiLaptop,
                description: 'Your fleet, coverage, and device details in one place.',
                link: '/your-account/product-registration',
                action: 'Manage products',
                rows: this.products.slice(0, 3).map((product, index) => ({
                    id: `${product.sn}-${product.orgId || ''}-${index}`,
                    title: product.model || 'Getac device',
                    detail: [`Serial ${product.sn || 'Unavailable'}`, product.orgName].filter(Boolean).join(' · '),
                    ...this.warrantyStatus(product.inwarranty)
                })),
                footnote: this.loading
                    ? 'Loading your registered products…'
                    : this.error
                    ? 'Registered products are temporarily unavailable'
                    : `Showing ${Math.min(this.products.length, 3)} of ${this.products.length} devices · Recently registered first`
            }
        }
    },
    mounted() {
        this.fetchProducts()
    },
    methods: {
        warrantyStatus(value) {
            const normalizedValue = String(value).trim().toLowerCase()
            if (value === true || ['true', 'yes'].includes(normalizedValue)) {
                return { status: 'In warranty', tone: 'green' }
            }
            if (value === false || ['false', 'no'].includes(normalizedValue)) {
                return { status: 'Out of warranty', tone: 'orange' }
            }
            return { status: value || 'Coverage unavailable', tone: 'neutral' }
        },
        emitSummary() {
            this.$emit('summary', {
                metric: this.loading ? '…' : this.error ? '—' : String(this.products.length).padStart(2, '0'),
                metricLabel: 'Registered devices'
            })
        },
        async fetchProducts() {
            if (this.fetchingProducts) return
            this.fetchingProducts = true
            this.loading = true
            this.error = ''
            this.emitSummary()
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query DashboardRegisteredProducts {
                            queryRegisteredSNList {
                                sn
                                assetTag
                                location
                                inwarranty
                                registerDate
                                model
                                orgName
                                orgId
                            }
                        }
                    `,
                    fetchPolicy: 'network-only'
                })
                if (!Array.isArray(data?.queryRegisteredSNList)) throw new Error('Registered products unavailable')
                const timestamp = (value) => Date.parse(value) || 0
                this.products = [...data.queryRegisteredSNList].sort(
                    (a, b) => timestamp(b.registerDate) - timestamp(a.registerDate)
                )
            } catch (error) {
                this.products = []
                this.error = 'Your registered products could not be loaded. Please try again.'
            } finally {
                this.fetchingProducts = false
                this.loading = false
                this.emitSummary()
            }
        }
    }
}
</script>

<template>
    <dashboard-section
        :section="section"
        :loading="loading"
        :error="error"
        loading-text="Loading your registered products…"
        empty-text="No registered products yet. Register a device to see it here."
        @retry="fetchProducts"
    />
</template>
